import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Apple,
  Edit3,
  Eye,
  EyeOff,
  Loader2,
  MonitorDown,
  Package,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReleaseFormDialog } from "@/components/release-form-dialog";
import { cn } from "@/lib/cn";
import {
  deleteRelease,
  listAdminReleases,
  type Release,
} from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";

export function AdminReleasesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Release | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const releasesQuery = useQuery({
    queryKey: ["releases-admin"],
    queryFn: listAdminReleases,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRelease(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["releases-admin"] });
      void queryClient.invalidateQueries({ queryKey: ["releases-public"] });
      setDeletingId(null);
    },
    onError: (err) => {
      alert(extractErrorMessage(err, "삭제에 실패했습니다"));
      setDeletingId(null);
    },
  });

  const releases = releasesQuery.data ?? [];

  const onSaved = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["releases-admin"] });
    void queryClient.invalidateQueries({ queryKey: ["releases-public"] });
  };

  return (
    <div className="space-y-6 p-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">관리자</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">릴리스 관리</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            데스크톱 앱 빌드 URL 을 등록·관리합니다. R2 등에 업로드한 후 이곳에 URL 을
            붙여 넣으면 다운로드 페이지에 자동 노출됩니다.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="gap-2"
        >
          <Plus className="h-4 w-4" /> 새 릴리스
        </Button>
      </header>

      <ReleaseFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={onSaved}
      />
      <ReleaseFormDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
        {...(editing ? { initial: editing } : {})}
      />

      {releasesQuery.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> 불러오는 중…
        </p>
      )}

      {!releasesQuery.isLoading && releases.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <Package className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
          <p className="mt-3 font-medium">아직 릴리스가 없습니다</p>
          <p className="mt-1 text-sm text-muted-foreground">
            "새 릴리스" 버튼으로 첫 버전을 등록해 보세요.
          </p>
        </div>
      )}

      {!releasesQuery.isLoading && releases.length > 0 && (
        <ul className="space-y-3">
          {releases.map((r) => (
            <li
              key={r.id}
              className="rounded-lg border border-border bg-card p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-base font-semibold">
                      v{r.version}
                    </span>
                    <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {r.channel}
                    </span>
                    {r.published ? (
                      <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-600 dark:text-emerald-400">
                        <Eye className="h-3 w-3" /> 공개
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        <EyeOff className="h-3 w-3" /> 비공개
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    게시: {new Date(r.publishedAt).toLocaleString()}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3 text-xs">
                    <OsBadge
                      ok={Boolean(r.macArm64Url)}
                      icon={Apple}
                      label="mac arm64"
                    />
                    <OsBadge
                      ok={Boolean(r.macX64Url)}
                      icon={Apple}
                      label="mac x64"
                    />
                    <OsBadge
                      ok={Boolean(r.windowsUrl)}
                      icon={MonitorDown}
                      label="windows"
                    />
                  </div>
                  {r.notes && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                        릴리스 노트 보기
                      </summary>
                      <pre className="mt-2 whitespace-pre-wrap rounded bg-muted/50 p-3 text-xs">
                        {r.notes}
                      </pre>
                    </details>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditing(r)}
                  >
                    <Edit3 className="h-3.5 w-3.5" /> 수정
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={deleteMutation.isPending && deletingId === r.id}
                    onClick={() => {
                      if (
                        confirm(
                          `정말 v${r.version} 릴리스를 삭제할까요? 다운로드 페이지에서 즉시 사라집니다.`,
                        )
                      ) {
                        setDeletingId(r.id);
                        deleteMutation.mutate(r.id);
                      }
                    }}
                  >
                    {deleteMutation.isPending && deletingId === r.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                    삭제
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OsBadge({
  ok,
  icon: Icon,
  label,
}: {
  ok: boolean;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded px-2 py-0.5",
        ok
          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          : "bg-muted text-muted-foreground line-through",
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
